import type { CountryWithRegionDto } from "app/shared/api/getCountriesWithRegions";
import type { RegionDto } from "app/shared/api/getRegions";
import * as AT from "./actionTypes";

export interface RegionManagerState {
  countries: CountryWithRegionDto[];
  regions: RegionDto[];
  countriesLoading: boolean;
  countriesError?: string;
  regionsLoading: boolean;
  regionsError?: string;

  inheritFromDivision: boolean;
  sourceDivisionId: number | null;
  persistedInheritedFromDivisionId: number | null;

  countryEdits: Record<string, number | null>;
  selectedCountryIds: string[];

  searchTerm: string;
  sortBy: "country" | "region";
  sortOrder: "asc" | "desc";

  viewRegionsModalOpen: boolean;

  saving: boolean;
  saveError?: string;
  saveSuccessUnassigned: Array<{ countryId: number; countryCode: string; countryName: string }>;
}

const initialState: RegionManagerState = {
  countries: [],
  regions: [],
  countriesLoading: false,
  countriesError: undefined,
  regionsLoading: false,
  regionsError: undefined,

  inheritFromDivision: false,
  sourceDivisionId: null,
  persistedInheritedFromDivisionId: null,

  countryEdits: {},
  selectedCountryIds: [],

  searchTerm: "",
  sortBy: "country",
  sortOrder: "asc",

  viewRegionsModalOpen: false,

  saving: false,
  saveError: undefined,
  saveSuccessUnassigned: [],
};

function normalizeId(v: string | number): string {
  return String(v);
}

export default function reducer(
  state: RegionManagerState = initialState,
  action: { type: string; payload?: any }
): RegionManagerState {
  switch (action.type) {
    case AT.REGION_MANAGER_LOAD_COUNTRIES_REQUEST:
      return { ...state, countriesLoading: true, countriesError: undefined };

    case AT.REGION_MANAGER_LOAD_COUNTRIES_SUCCESS: {
      const countries = action.payload ?? [];
      return {
        ...state,
        countries,
        countriesLoading: false,
        countriesError: undefined,
      };
    }

    case AT.REGION_MANAGER_LOAD_COUNTRIES_FAILURE:
      return {
        ...state,
        countriesLoading: false,
        countriesError: action.payload?.error,
      };

    case AT.REGION_MANAGER_LOAD_REGIONS_REQUEST:
      return { ...state, regionsLoading: true, regionsError: undefined };

    case AT.REGION_MANAGER_LOAD_REGIONS_SUCCESS:
      return {
        ...state,
        regions: action.payload ?? [],
        regionsLoading: false,
        regionsError: undefined,
      };

    case AT.REGION_MANAGER_LOAD_REGIONS_FAILURE:
      return {
        ...state,
        regionsLoading: false,
        regionsError: action.payload?.error,
      };

    case AT.REGION_MANAGER_SET_INHERIT: {
      const inheritFromDivision = !!action.payload;
      return {
        ...state,
        inheritFromDivision,
        ...(inheritFromDivision ? {} : { sourceDivisionId: null }),
      };
    }

    case AT.REGION_MANAGER_SET_SOURCE_DIVISION:
      return { ...state, sourceDivisionId: action.payload ?? null };

    case AT.REGION_MANAGER_INHERITANCE_LOADED: {
      const inheritedFromDivisionId = action.payload?.inheritedFromDivisionId ?? null;
      return {
        ...state,
        persistedInheritedFromDivisionId: inheritedFromDivisionId,
        inheritFromDivision: inheritedFromDivisionId != null,
        sourceDivisionId: inheritedFromDivisionId,
      };
    }

    case AT.REGION_MANAGER_INHERITANCE_SAVED: {
      const inheritedFromDivisionId = action.payload?.inheritedFromDivisionId ?? null;
      return {
        ...state,
        saving: false,
        saveError: undefined,
        persistedInheritedFromDivisionId: inheritedFromDivisionId,
        saveSuccessUnassigned: [],
      };
    }

    case AT.REGION_MANAGER_SET_COUNTRY_REGION: {
      const { countryId, regionId } = action.payload;
      const key = normalizeId(countryId);
      const countryEdits = { ...state.countryEdits, [key]: regionId };
      return { ...state, countryEdits };
    }

    case AT.REGION_MANAGER_SET_BULK_REGION: {
      const { countryIds, regionId } = action.payload;
      const countryEdits = { ...state.countryEdits };
      (countryIds || []).forEach((id: string | number) => {
        countryEdits[normalizeId(id)] = regionId;
      });
      return { ...state, countryEdits };
    }

    case AT.REGION_MANAGER_SELECT_COUNTRIES:
      return { ...state, selectedCountryIds: [...(action.payload || [])].map(normalizeId) };

    case AT.REGION_MANAGER_SELECT_ALL:
      return { ...state, selectedCountryIds: (action.payload || []).map(normalizeId) };

    case AT.REGION_MANAGER_UNSELECT_ALL:
      return { ...state, selectedCountryIds: [] };

    case AT.REGION_MANAGER_SET_SEARCH:
      return { ...state, searchTerm: action.payload ?? "" };

    case AT.REGION_MANAGER_SET_SORT:
      return {
        ...state,
        sortBy: action.payload?.sortBy ?? "country",
        sortOrder: action.payload?.sortOrder ?? "asc",
      };

    case AT.REGION_MANAGER_DISCARD_CHANGES:
      return { ...state, countryEdits: {}, selectedCountryIds: [] };

    case AT.REGION_MANAGER_OPEN_VIEW_REGIONS:
      return { ...state, viewRegionsModalOpen: true };

    case AT.REGION_MANAGER_CLOSE_VIEW_REGIONS:
      return { ...state, viewRegionsModalOpen: false };

    case AT.REGION_MANAGER_REGIONS_SAVED:
      return { ...state, regions: action.payload ?? state.regions, viewRegionsModalOpen: false };

    case AT.REGION_MANAGER_SAVE_REQUEST:
      return { ...state, saving: true, saveError: undefined, saveSuccessUnassigned: [] };

    case AT.REGION_MANAGER_SAVE_SUCCESS:
      return {
        ...state,
        saving: false,
        saveError: undefined,
        countryEdits: {},
        saveSuccessUnassigned: action.payload?.unassignedCountires ?? [],
      };

    case AT.REGION_MANAGER_SAVE_FAILURE:
      return {
        ...state,
        saving: false,
        saveError: action.payload?.error,
        saveSuccessUnassigned: [],
      };

    default:
      return state;
  }
}